import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import {
  CAPTURE_AFTER_BEHAVIOR_LABELS,
  CAPTURE_AFTER_BEHAVIORS,
  DEFAULT_CAPTURE_PREFERENCES,
  sanitizeCapturePreferences,
} from "../constants/capturePreferences";
import "./DeleteWordModal.css";
import "./RegisterSettingsModal.css";
import "./EntrainementVoc.css";

function RadioChip({ name, label, checked, onChange }) {
  return (
    <label
      className={`entrainement-chip entrainement-chip--round${
        checked ? " entrainement-chip--checked" : ""
      }`}
    >
      <input
        type="radio"
        className="entrainement-chip__input"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <span className="entrainement-chip__marker" aria-hidden="true" />
      <span className="entrainement-chip__label">{label}</span>
    </label>
  );
}

export default function CaptureSettingsModal({ open, value, onChange, onClose }) {
  const [draft, setDraft] = useState(DEFAULT_CAPTURE_PREFERENCES);

  useEffect(() => {
    if (!open) return;
    setDraft(sanitizeCapturePreferences(value));
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const handleApply = () => {
    onChange?.(sanitizeCapturePreferences(draft));
    onClose?.();
  };

  const selectBehavior = (behavior) => {
    setDraft((prev) => ({ ...prev, afterCaptureBehavior: behavior }));
  };

  return createPortal(
    <div
      className="delete-word-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="delete-word-modal register-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="capture-settings-modal-title"
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <h3
          id="capture-settings-modal-title"
          className="delete-word-modal__title delete-word-modal__title--gothic"
        >
          Paramètres de capture de mot
        </h3>

        <div className="register-settings-modal__body">
          <section className="register-settings-modal__section">
            <h4 className="register-settings-modal__section-title">
              À la fin de la capture d'un mot
            </h4>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid"
              role="radiogroup"
              aria-label="Comportement après la capture d'un mot"
            >
              <RadioChip
                name="capture-after-behavior"
                label={CAPTURE_AFTER_BEHAVIOR_LABELS[CAPTURE_AFTER_BEHAVIORS.REGISTER]}
                checked={draft.afterCaptureBehavior === CAPTURE_AFTER_BEHAVIORS.REGISTER}
                onChange={() => selectBehavior(CAPTURE_AFTER_BEHAVIORS.REGISTER)}
              />
              <RadioChip
                name="capture-after-behavior"
                label={CAPTURE_AFTER_BEHAVIOR_LABELS[CAPTURE_AFTER_BEHAVIORS.STAY]}
                checked={draft.afterCaptureBehavior === CAPTURE_AFTER_BEHAVIORS.STAY}
                onChange={() => selectBehavior(CAPTURE_AFTER_BEHAVIORS.STAY)}
              />
            </div>
          </section>
        </div>

        <div className="delete-word-modal__actions register-settings-modal__actions">
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--confirm"
            onClick={handleApply}
          >
            Appliquer
          </button>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
